import { useRouter } from '@tanstack/react-router';
import { useAtomValue, useSetAtom } from 'jotai';

import { queryClient } from '@/shared/lib/queryClient';

import {
  isAuthenticatedDerivedAtom,
  type SessionData,
  sessionAtom,
  userRoleDerivedAtom,
} from '../atoms/sessionAtom';

/**
 * Hook de acesso à sessão do usuário autenticado.
 *
 * Fornece a sessão atual, status de autenticação e ações de login/logout.
 *
 * @example
 * ```tsx
 * const { session, isAuthenticated, logout } = useSession();
 *
 * if (!isAuthenticated) return <Navigate to="/login" />;
 * return <p>Olá, {session.name}</p>;
 * ```
 */
export function useSession() {
  const session = useAtomValue(sessionAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedDerivedAtom);
  const role = useAtomValue(userRoleDerivedAtom);
  const setSession = useSetAtom(sessionAtom);

  function login(data: SessionData) {
    setSession(data);
  }

  const router = useRouter();

  function logout() {
    setSession(null);
    queryClient.clear();
    void router.navigate({ to: '/login' });
  }

  return { session, isAuthenticated, role, login, logout } as const;
}
